// The ONE vendor form (D107.2): the Vendors page's Add / Edit dialog and the "+ Create new vendor"
// popup every vendor picker offers (CreateVendorModal) are both this component, so a vendor is
// validated, checked for look-alikes and written the same way wherever it is made.
//
// Create goes through `createVendor` (lib/vendorsApi): a name that looks like a vendor already on
// file comes back 409 VENDOR_LOOKALIKE (owner 2026-10-02), the person is asked, and the request is
// resent with `confirmDistinct` only when they say it is a different supplier. Edit (`editingId`)
// is a PATCH. The form only saves; what happens next (reload a list, close the dialog, go back to
// an invoice) is the host's `onSaved`.
import React, { useState } from 'react';
import { PhoneInput, defaultCountries, removeDialCode } from 'react-international-phone';
import 'react-international-phone/style.css';
import Button from '../../components/ui/Button';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { FIELD as FIELD_CLASS } from '../../components/ui/tokens';
import { patch } from '../../lib/apiClient';
import { fieldErrorsOf } from '../../lib/apiErrors';
import { createVendor, resetVendorOptionsCache, type NewVendorPayload } from '../../lib/vendorsApi';

// Vendors are Trinidad & Tobago only — lock the phone input to that country's flag/code.
const TRINIDAD_ONLY_COUNTRY = defaultCountries.filter((c) => c[1] === 'tt');
const PHONE_INPUT_CSS_VARS = {
  '--react-international-phone-height': '38px',
  '--react-international-phone-border-radius': '6px',
} as React.CSSProperties;

export type VendorFormValues = {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
  address: string;
  creditDays: string;
  code: string;
  isActive: boolean;
  priceListUrl: string;
  /** D119.6: copied onto each new PO to this vendor; existing POs keep their own. */
  hideVesselOnPo: boolean;
};

type FormErrors = Partial<Record<keyof VendorFormValues, string>>;

const EMPTY_FORM: VendorFormValues = {
  name: '',
  email: '',
  countryCode: '+1868',
  phone: '',
  address: '',
  creditDays: '',
  code: '',
  isActive: true,
  priceListUrl: '',
  hideVesselOnPo: false,
};

/** The fields the form shows, in order. A server error for any other field is shown as the form's own message. */
const FIELDS = ['name', 'email', 'phone', 'address', 'creditDays', 'code'] as const;

/** Sentence-case labels for the fields the form loops over (docs/DESIGN.md). */
const FIELD_LABELS: Record<(typeof FIELDS)[number], string> = {
  name: 'Vendor name',
  email: 'Email',
  phone: 'Contact number',
  address: 'Address',
  creditDays: 'Credit days',
  code: 'Vendor code',
};

/* ------------------------------
   Validation helpers
------------------------------ */
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const PHONE_RULES: Record<string, { min: number; max: number }> = {
  '+1868': { min: 7, max: 7 },
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+971': { min: 9, max: 9 },
  '+61': { min: 9, max: 9 },
};

function validate(form: VendorFormValues): FormErrors {
  const e: FormErrors = {};

  const name = form.name.trim();
  const email = form.email.trim();
  const phone = form.phone.trim();
  const address = form.address.trim();
  const creditDays = form.creditDays.trim();

  if (!name) e.name = 'Vendor Name is required';
  else if (name.length < 3) e.name = 'Minimum 3 characters required';
  else if (/^[0-9]+$/.test(name)) e.name = 'Name cannot be numeric only';
  else if (!/[a-zA-Z]/.test(name)) e.name = 'Name must contain letters';

  // D86: only the name is required. Email, phone and address are checked only when typed.
  if (email && /\s/.test(email)) e.email = 'Email cannot contain spaces';
  else if (email && !EMAIL_RE.test(email)) e.email = 'Invalid email format';

  if (!phone) {
    // optional
  } else if (!/^\d+$/.test(phone)) e.phone = 'Only numbers allowed';
  else {
    const r = PHONE_RULES[form.countryCode];
    if (r && (phone.length < r.min || phone.length > r.max)) {
      e.phone = `Invalid number length for ${form.countryCode}`;
    }
  }

  if (address && address.length < 10) e.address = 'Minimum 10 characters required';

  // Blank is allowed: it means the supplier's terms are not on file (NEW-2).
  if (creditDays && !/^\d+$/.test(creditDays)) e.creditDays = 'Only whole numbers allowed';

  // Code is optional — blank means "suggest one from the name" on create, and
  // legacy vendors are allowed to have none.
  const code = form.code.trim().toUpperCase();
  if (code && !/^[A-Z0-9]{2,6}$/.test(code)) {
    e.code = 'Use 2-6 letters or digits (example: MD)';
  }

  return e;
}

export interface VendorFormProps {
  /** Starting values (an invoice's printed vendor, or the vendor being edited); the rest start blank. */
  initial?: Partial<VendorFormValues>;
  /** Edit this vendor (PATCH). Left out, the form creates one. */
  editingId?: string;
  /** The save went through. `id` is the new vendor's id; null for an edit. */
  onSaved: (saved: { id: string | null; name: string }) => void | Promise<void>;
  onCancel: () => void;
}

export const VendorForm: React.FC<VendorFormProps> = ({ initial, editingId, onSaved, onCancel }) => {
  const confirm = useConfirm();
  const [form, setForm] = useState<VendorFormValues>(() => ({ ...EMPTY_FORM, ...initial }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * POST a new vendor. When the server says it may already be on file (VENDOR_LOOKALIKE) ask,
   * and resend with `confirmDistinct` only when the person says it is a different supplier.
   * @returns the created vendor, or null when the person stepped back
   */
  const createWithLookalikeCheck = async (payload: NewVendorPayload) => {
    try {
      return await createVendor(payload);
    } catch (err: any) {
      if (err?.code !== 'VENDOR_LOOKALIKE') throw err;
      const matches: Array<{ field?: string; name?: string }> = Array.isArray(err.validationErrors) ? err.validationErrors : [];
      const distinct = await confirm({
        title: 'This vendor may already be on the list',
        summary: 'Adding a second row for one supplier splits its invoices, prices and payables. Use the existing vendor unless this really is a different company.',
        itemsHeading: 'Already on the list',
        items: matches.map((m) => ({
          label: m.field === 'address' ? 'Same address' : 'Similar name',
          text: m.name || '',
        })),
        confirmLabel: 'Create anyway',
        cancelLabel: 'Go back',
      });
      if (!distinct) return null;
      return createVendor(payload, { confirmDistinct: true });
    }
  };

  const onSave = async () => {
    const v = validate(form);
    setErrors(v);
    setFormError(null);
    if (Object.keys(v).length > 0) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() ? `${form.countryCode} ${form.phone}`.trim() : '',
        address: form.address.trim(),
        creditDays: Number(form.creditDays || 0),
        code: form.code.trim().toUpperCase(),
        isActive: form.isActive,
        priceListUrl: form.priceListUrl.trim(),
        hideVesselOnPo: form.hideVesselOnPo,
      };

      let savedId: string | null = null;
      if (editingId) {
        await patch(`/vendors/${editingId}`, payload);
        // D86: the supplier pickers share one cached list; drop it so the change shows at once.
        resetVendorOptionsCache();
      } else {
        const created = await createWithLookalikeCheck(payload);
        if (!created) return; // the person kept the form open
        savedId = created._id;
      }
      await onSaved({ id: savedId, name: payload.name });
    } catch (err: any) {
      // The server names the field ({errors:[{field,message}]}, kept by apiClient as
      // `validationErrors`); a refusal naming a field the form does not show is said as a message.
      const fieldErrors: FormErrors = {};
      for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
        if ((FIELDS as readonly string[]).includes(field)) fieldErrors[field as keyof VendorFormValues] = message;
      }
      setErrors(fieldErrors);
      if (!Object.keys(fieldErrors).length) setFormError(err?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {FIELDS.map((f) => (
        <div key={f}>
          <label className="block text-sm font-medium text-slate-700 mb-1">{FIELD_LABELS[f]}</label>
          {f === 'code' ? (
            <>
              <input
                value={form.code}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) })
                }
                placeholder="e.g. MD"
                className={`${FIELD_CLASS} h-10 ${errors.code ? 'border-red-300' : 'border-slate-200'}`}
              />
              <div className="text-xs text-slate-500 mt-1">
                Initials shown against client invoice lines for traceability. Leave blank to
                auto-generate.
              </div>
            </>
          ) : f === 'phone' ? (
            <PhoneInput
              defaultCountry="tt"
              value={`${form.countryCode}${form.phone}`}
              onChange={(phone, meta) => {
                const digits = removeDialCode({ phone, dialCode: meta.country.dialCode }).replace(/\D/g, '');
                setForm({ ...form, countryCode: `+${meta.country.dialCode}`, phone: digits });
              }}
              hideDropdown
              countries={TRINIDAD_ONLY_COUNTRY}
              forceDialCode
              style={PHONE_INPUT_CSS_VARS}
              className="w-full"
              inputClassName={`!w-full ${errors.phone ? '!border-red-300' : ''}`}
            />
          ) : f === 'address' ? (
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={`${FIELD_CLASS} min-h-[90px] py-2 resize-none ${
                errors.address ? 'border-red-300' : 'border-slate-200'
              }`}
            />
          ) : f === 'creditDays' ? (
            <input
              type="number"
              min="0"
              step="1"
              value={form.creditDays}
              onChange={(e) => setForm({ ...form, creditDays: e.target.value })}
              className={`${FIELD_CLASS} h-10 ${errors.creditDays ? 'border-red-300' : 'border-slate-200'}`}
              placeholder="Blank = not on file"
            />
          ) : (
            <input
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              className={`${FIELD_CLASS} h-10 ${errors[f] ? 'border-red-300' : 'border-slate-200'}`}
            />
          )}
          {errors[f] && <div className="text-xs text-red-600 mt-1">{errors[f]}</div>}
        </div>
      ))}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={form.hideVesselOnPo}
            onChange={(e) => setForm({ ...form, hideVesselOnPo: e.target.checked })}
          />
          Hide vessel name on POs
        </label>
        <div className="text-xs text-slate-500 mt-1">
          For a supplier that is also a ship chandler. New POs to this vendor leave the vessel name off the
          PDF and Excel; each PO can still be changed, and POs already made are not changed.
        </div>
      </div>
      {formError && (
        <div role="alert" className="text-sm text-red-600">
          {formError}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={onSave} loading={saving}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default VendorForm;
